import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { doctorAPI, usersAPI, rolesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import TouchTimePicker from '../components/TouchTimePicker';
import {
  Stethoscope,
  Plus,
  Edit2,
  DollarSign,
  Clock,
  Calendar,
  User,
  Percent,
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

export const DoctorConfigPage = () => {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [formData, setFormData] = useState({
    user_id: '',
    consultation_fee: '',
    follow_up_fee: '',
    revenue_share_percent: '',
    specialization: '',
    qualification: '',
    experience_years: '',
    registration_number: '',
    slot_duration: 15,
    max_patients_per_day: 50,
    availability: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const isHROrAdmin = user?.role_name?.toLowerCase().includes('admin') || user?.role_name?.toLowerCase() === 'hr';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [doctorsRes, usersRes] = await Promise.all([
        doctorAPI.list(),
        usersAPI.list(),
      ]);
      setDoctors(doctorsRes.data);
      // Filter users who have doctor role
      const doctorUsers = usersRes.data.filter(u => 
        u.role_name?.toLowerCase().includes('doctor')
      );
      setUsers(doctorUsers);
    } catch (err) {
      toast.error('Failed to load doctor configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        ...formData,
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        follow_up_fee: parseFloat(formData.follow_up_fee) || 0,
        revenue_share_percent: parseFloat(formData.revenue_share_percent) || 0,
        experience_years: parseInt(formData.experience_years) || 0,
      };
      await doctorAPI.create(data);
      toast.success('Doctor configuration created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = {
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        follow_up_fee: parseFloat(formData.follow_up_fee) || 0,
        revenue_share_percent: parseFloat(formData.revenue_share_percent) || 0,
        specialization: formData.specialization,
        qualification: formData.qualification,
        experience_years: parseInt(formData.experience_years) || 0,
        registration_number: formData.registration_number,
        slot_duration: formData.slot_duration,
        max_patients_per_day: formData.max_patients_per_day,
        availability: formData.availability,
      };
      await doctorAPI.update(selectedDoctor.id, data);
      toast.success('Doctor configuration updated successfully');
      setIsEditDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (doctor) => {
    setSelectedDoctor(doctor);
    setFormData({
      user_id: doctor.user_id,
      consultation_fee: doctor.consultation_fee?.toString() || '',
      follow_up_fee: doctor.follow_up_fee?.toString() || '',
      revenue_share_percent: doctor.revenue_share_percent?.toString() || '',
      specialization: doctor.specialization || '',
      qualification: doctor.qualification || '',
      experience_years: doctor.experience_years?.toString() || '',
      registration_number: doctor.registration_number || '',
      slot_duration: doctor.slot_duration || 15,
      max_patients_per_day: doctor.max_patients_per_day || 50,
      availability: doctor.availability || [],
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      consultation_fee: '',
      follow_up_fee: '',
      revenue_share_percent: '',
      specialization: '',
      qualification: '',
      experience_years: '',
      registration_number: '',
      slot_duration: 15,
      max_patients_per_day: 50,
      availability: [],
    });
    setSelectedDoctor(null);
  };

  const toggleAvailability = (day) => {
    const existing = formData.availability.find(a => a.day === day);
    if (existing) {
      setFormData({
        ...formData,
        availability: formData.availability.filter(a => a.day !== day),
      });
    } else {
      setFormData({
        ...formData,
        availability: [...formData.availability, { day, start: '09:00', end: '17:00' }],
      });
    }
  };

  const updateAvailabilityTime = (day, field, value) => {
    setFormData({
      ...formData,
      availability: formData.availability.map(a => 
        a.day === day ? { ...a, [field]: value } : a
      ),
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doctor-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Doctor Configuration
          </h1>
          <p className="mt-1 text-slate-600">Manage doctor fees, schedules, and revenue sharing</p>
        </div>
        {isHROrAdmin && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-doctor-config-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Doctor Config
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Doctor Configuration</DialogTitle>
                <DialogDescription>Set up fees and availability for a doctor</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Select Doctor *</Label>
                    <Select
                      value={formData.user_id}
                      onValueChange={(value) => setFormData({ ...formData, user_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a doctor" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => !doctors.find(d => d.user_id === u.id)).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            Dr. {u.first_name} {u.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Specialization</Label>
                      <Input
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        placeholder="e.g., Cardiology"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Qualification</Label>
                      <Input
                        value={formData.qualification}
                        onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                        placeholder="e.g., MBBS, MD"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Consultation Fee (₹)</Label>
                      <Input
                        type="number"
                        value={formData.consultation_fee}
                        onChange={(e) => setFormData({ ...formData, consultation_fee: e.target.value })}
                        placeholder="500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Follow-up Fee (₹)</Label>
                      <Input
                        type="number"
                        value={formData.follow_up_fee}
                        onChange={(e) => setFormData({ ...formData, follow_up_fee: e.target.value })}
                        placeholder="300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Revenue Share (%)</Label>
                      <Input
                        type="number"
                        value={formData.revenue_share_percent}
                        onChange={(e) => setFormData({ ...formData, revenue_share_percent: e.target.value })}
                        placeholder="70"
                        max="100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Experience (Years)</Label>
                      <Input
                        type="number"
                        value={formData.experience_years}
                        onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                        placeholder="10"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Registration No.</Label>
                      <Input
                        value={formData.registration_number}
                        onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                        placeholder="MCI-12345"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Slot Duration (min)</Label>
                      <Select
                        value={formData.slot_duration.toString()}
                        onValueChange={(value) => setFormData({ ...formData, slot_duration: parseInt(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 mins</SelectItem>
                          <SelectItem value="15">15 mins</SelectItem>
                          <SelectItem value="20">20 mins</SelectItem>
                          <SelectItem value="30">30 mins</SelectItem>
                          <SelectItem value="45">45 mins</SelectItem>
                          <SelectItem value="60">60 mins</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>Availability Schedule</Label>
                    <div className="space-y-2">
                      {DAYS_OF_WEEK.map((day) => {
                        const dayAvail = formData.availability.find(a => a.day === day.value);
                        return (
                          <div key={day.value} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                            <Checkbox
                              checked={!!dayAvail}
                              onCheckedChange={() => toggleAvailability(day.value)}
                            />
                            <span className="w-24 font-medium">{day.label}</span>
                            {dayAvail && (
                              <>
                                <TouchTimePicker
                                  value={dayAvail.start}
                                  onChange={(v) => updateAvailabilityTime(day.value, 'start', v)}
                                  className="w-32"
                                  testId={`docconf-start-${day.value}`}
                                />
                                <span>to</span>
                                <TouchTimePicker
                                  value={dayAvail.end}
                                  onChange={(v) => updateAvailabilityTime(day.value, 'end', v)}
                                  className="w-32"
                                  testId={`docconf-end-${day.value}`}
                                />
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Configuration'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Doctor Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {doctors.map((doctor, index) => (
          <Card 
            key={doctor.id} 
            className="card-hover animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`doctor-card-${doctor.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Stethoscope className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      Dr. {doctor.user?.first_name} {doctor.user?.last_name}
                    </CardTitle>
                    <CardDescription>{doctor.specialization || 'General'}</CardDescription>
                  </div>
                </div>
                {isHROrAdmin && (
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(doctor)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                      <DollarSign className="h-4 w-4" />
                      Consultation
                    </div>
                    <p className="font-bold text-lg">₹{doctor.consultation_fee || 0}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                      <Percent className="h-4 w-4" />
                      Revenue Share
                    </div>
                    <p className="font-bold text-lg">{doctor.revenue_share_percent || 0}%</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-slate-500">Availability</p>
                  <div className="flex flex-wrap gap-1">
                    {doctor.availability?.length > 0 ? (
                      doctor.availability.map((avail) => (
                        <Badge key={avail.day} variant="outline" className="text-xs capitalize">
                          {avail.day.slice(0, 3)}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">Not configured</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Clock className="h-4 w-4" />
                    {doctor.slot_duration} min slots
                  </div>
                  <Badge className={doctor.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                    {doctor.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {doctors.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Stethoscope className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Doctor Configurations</h3>
              <p className="text-slate-500 mt-1">Add doctor configurations to manage fees and schedules.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Doctor Configuration</DialogTitle>
            <DialogDescription>Update fees and availability</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Specialization</Label>
                  <Input
                    value={formData.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Qualification</Label>
                  <Input
                    value={formData.qualification}
                    onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Consultation Fee (₹)</Label>
                  <Input
                    type="number"
                    value={formData.consultation_fee}
                    onChange={(e) => setFormData({ ...formData, consultation_fee: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Follow-up Fee (₹)</Label>
                  <Input
                    type="number"
                    value={formData.follow_up_fee}
                    onChange={(e) => setFormData({ ...formData, follow_up_fee: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Revenue Share (%)</Label>
                  <Input
                    type="number"
                    value={formData.revenue_share_percent}
                    onChange={(e) => setFormData({ ...formData, revenue_share_percent: e.target.value })}
                    max="100"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>Availability Schedule</Label>
                <div className="space-y-2">
                  {DAYS_OF_WEEK.map((day) => {
                    const dayAvail = formData.availability.find(a => a.day === day.value);
                    return (
                      <div key={day.value} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                        <Checkbox
                          checked={!!dayAvail}
                          onCheckedChange={() => toggleAvailability(day.value)}
                        />
                        <span className="w-24 font-medium">{day.label}</span>
                        {dayAvail && (
                          <>
                            <TouchTimePicker
                              value={dayAvail.start}
                              onChange={(v) => updateAvailabilityTime(day.value, 'start', v)}
                              className="w-32"
                              testId={`docconf-edit-start-${day.value}`}
                            />
                            <span>to</span>
                            <TouchTimePicker
                              value={dayAvail.end}
                              onChange={(v) => updateAvailabilityTime(day.value, 'end', v)}
                              className="w-32"
                              testId={`docconf-edit-end-${day.value}`}
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorConfigPage;
